import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/lib/testIds";
import SeoHead from "@/components/SeoHead";
import FinalCta from "@/components/sections/FinalCta";
import { ArrowUpRight } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=2200&q=80";

export default function About() {
  const { t } = useI18n();
  const principles = t("about_page.principles");

  return (
    <div data-testid={TID.pageAbout}>
      <SeoHead titleKey="seo.about_title" descKey="seo.about_desc" />
      {/* Page hero */}
      <section className="relative bg-[color:var(--gc-navy)] text-white pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={BG} alt="" className="w-full h-full object-cover gc-kenburns" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--gc-navy)] via-[color:var(--gc-navy)]/90 to-transparent" />
        <div className="absolute inset-0 gc-grid-bg-dark opacity-60" />

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="gc-eyebrow text-white/60">{t("about_page.eyebrow")}</p>
            <h1 className="mt-6 font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.96] tracking-tight font-[700]">
              {t("about_page.title")}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 lg:col-start-2">
              <p className="text-xl md:text-2xl leading-relaxed text-[color:var(--gc-ink)] font-display font-[500]">
                {t("about_page.p1")}
              </p>
              <p className="mt-6 text-lg leading-relaxed text-[color:var(--gc-graphite)]">
                {t("about_page.p2")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-[color:var(--gc-paper)] py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <p className="gc-eyebrow">{t("about_page.eyebrow")}</p>
            <h2 className="mt-5 font-display text-4xl md:text-5xl tracking-tight font-[700] text-[color:var(--gc-ink)]">
              {t("about_page.principles_title")}
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-[color:var(--gc-line)] border border-[color:var(--gc-line)]">
            {Array.isArray(principles) &&
              principles.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="relative bg-white p-8 lg:p-12 group"
                >
                  <span className="font-mono text-[11px] tracking-[0.18em] text-[color:var(--gc-mist)]">
                    0{i + 1}
                  </span>
                  <h3 className="mt-6 font-display text-2xl lg:text-3xl font-[700] tracking-tight text-[color:var(--gc-ink)]">
                    {p.t}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--gc-steel)] max-w-md">
                    {p.d}
                  </p>
                  <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-[color:var(--gc-red)] group-hover:w-full transition-all duration-500" />
                </motion.div>
              ))}
          </div>

          <div className="mt-12">
            <Link to="/solutii" className="gc-btn gc-btn-outline-dark">
              {t("common.cta_solutions")}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Short market-experience section */}
      <section
        data-testid="section-experience-short"
        className="bg-white py-24 md:py-32 border-t border-[color:var(--gc-line)]"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5"
            >
              <p className="gc-eyebrow">{t("about_page.eyebrow")}</p>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.04] tracking-tight font-[700] text-[color:var(--gc-ink)]">
                {t("experience_short.title")}
              </h2>
              <div className="mt-8 h-px w-24 bg-[color:var(--gc-red)]" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-7 space-y-5"
            >
              <p className="text-lg md:text-xl leading-relaxed text-[color:var(--gc-ink)] font-display font-[500]">
                {t("experience_short.p1")}
              </p>
              <p className="text-base lg:text-lg leading-relaxed text-[color:var(--gc-graphite)]">
                {t("experience_short.p2")}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <FinalCta />
    </div>
  );
}
